/**
 * Trust Center Wizard — TRUST_CENTER_SPEC.md (design blessed via the interactive
 * tree artifact, 2026-07-05). Anti-jargon binding table (§0.1) applies to every
 * string. Backend: /api/trustcenter (simulator-backed until the real Trust Hub
 * sequence lands behind the same endpoints).
 */
import React, { useEffect, useState } from 'react';
import { authFetch } from '../../auth';
import './TrustCenterWizard.css';

const BANNED = ['adult content', 'hate or harassment', 'alcohol, firearms, tobacco, drugs', 'gambling', 'scams or misleading offers'];

const TrustCenterWizard = ({ user }) => {
  const [screen, setScreen] = useState('status'); // status decides: intro or dashboard
  const [regs, setRegs] = useState(null);
  const [holder, setHolder] = useState(null);     // 'person' | 'organization'
  const [bizName, setBizName] = useState('');
  const [bizCountry, setBizCountry] = useState('United States');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const userNumber = (user && user.freenumbers && user.freenumbers[0]) || '';

  const loadStatus = async () => {
    try {
      const r = await authFetch('/api/trustcenter/status');
      if (r.ok) {
        const d = await r.json();
        setRegs(d.registrations || []);
        setScreen((d.registrations || []).length ? 'dash' : 'intro');
      } else { setRegs([]); setScreen('intro'); }
    } catch (e) { setRegs([]); setScreen('intro'); }
  };
  useEffect(() => { loadStatus(); }, []);

  const submit = async (payload) => {
    setSubmitting(true); setError('');
    try {
      const r = await authFetch('/api/trustcenter/submit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ accountHolder: holder, userNumber, ...payload })
      });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.error || 'Submission failed');
      await loadStatus(); setScreen('dash');
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  const resubmit = async (regId) => {
    setSubmitting(true); setError('');
    try {
      const r = await authFetch('/api/trustcenter/resubmit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ regId, corrections: { businessName: bizName.replace(/REJECT/ig, '').trim() || 'Corrected LLC' } })
      });
      const d = await r.json();
      if (!r.ok || !d.success) throw new Error(d.error || 'Resubmit failed');
      await loadStatus();
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  const Blk = ({ head, more }) => (
    <div className="tc-blk"><p>{head}</p>
      <details><summary></summary><div>{more}</div></details>
    </div>
  );

  const screens = {
    intro: (
      <div data-testid="tc-intro">
        <h2 className="tc-h1">📱 Set up your new number</h2>
        <Blk head="✅ Your new number is ready — calls, voicemail, receiving texts, and your AI assistant."
             more="Everything except sending texts works the moment you get the number. Nothing to set up, nothing to wait for." />
        <Blk head="✍️ Only sending texts needs input from you — a one-time identity check."
             more="Phone networks only deliver texts from senders who have proven who they are — the same way a phone store asks for your ID before opening a plan. Nobody gets to use their network anonymously to annoy their customers. We do the paperwork; you answer a few questions." />
        <Blk head="⏱ Steps to enable sending texts to US mobile operators — about 3 minutes now, on in a few days."
             more="1 · Tell us if you're personally the account holder, or registering for another party (an entity with its own tax registration) — ~1 min. 2 · Confirm a few details — ~2 min. 3 · We submit it for you. 4 · Texting turns on in a few days (up to a week) — we'll notify you in Messages." />
        <button className="tc-cta" data-testid="tc-start" onClick={() => setScreen('router')}>Get started →</button>
        <button className="tc-link" onClick={() => setScreen('intl')}>Texting numbers outside the US &amp; Canada?</button>
      </div>
    ),
    router: (
      <div data-testid="tc-router">
        <h2 className="tc-h1">Who is the account holder for this number?</h2>
        <button className={`tc-choice ${holder === 'person' ? 'on' : ''}`} onClick={() => setHolder('person')}>
          <span className="cl">{holder === 'person' ? '●' : '○'} Me, personally</span>
          <span className="cs">The number is registered to me, and I pay for it myself.</span>
        </button>
        <button className={`tc-choice ${holder === 'organization' ? 'on' : ''}`} onClick={() => setHolder('organization')}>
          <span className="cl">{holder === 'organization' ? '●' : '○'} Another party</span>
          <span className="cs">An entity with its own tax registration — a company, LLC, or nonprofit — holds the account and pays for it.</span>
        </button>
        <div className="tc-note i">ⓘ Have a one-person LLC? If the LLC holds the account — even one used just for taxes — pick “Another party.” The approval then matches its tax paperwork and goes through more smoothly.</div>
        <button className="tc-cta" data-testid="tc-continue" disabled={!holder}
          onClick={() => setScreen(holder === 'person' ? 'pnet' : 'b1')}>Continue →</button>
        <button className="tc-link" onClick={() => setScreen('intro')}>← Back</button>
      </div>
    ),
    pnet: (
      <div>
        <h2 className="tc-h1">Quick check</h2>
        <p className="tc-lead">You're the account holder — no company, LLC, or nonprofit with its own tax ID holds this number?</p>
        <div className="tc-note w">A <b>one-person LLC counts</b> — many people have one just for taxes. If yours holds the account, that path approves more smoothly.</div>
        <button className="tc-cta" onClick={() => setScreen('p1')}>Right — it's me personally →</button>
        <button className="tc-cta ghost" onClick={() => { setHolder('organization'); setScreen('b1'); }}>Actually, another party → switch</button>
      </div>
    ),
    p1: (
      <div>
        <p className="tc-prog">Step 1 of 4 — About you</p>
        <h2 className="tc-h1">Tell us about yourself</h2>
        <div className="tc-fld"><label>Legal name</label><input placeholder="As shown on your government ID" /></div>
        <div className="tc-fld"><label>Your mobile number</label><input placeholder="Your real cell — not a NumFree number" />
          <p className="tc-help">We'll text a code here to confirm it's you.</p></div>
        <div className="tc-fld"><label>Home address</label><input placeholder="Street, city, state — US or Canada" />
          <p className="tc-help">No PO boxes. Personal setup is available to US &amp; Canada residents — elsewhere, use the organization path.</p></div>
        <button className="tc-cta" onClick={() => setScreen('p2')}>Continue →</button>
        <button className="tc-link" onClick={() => setScreen('router')}>← Back</button>
      </div>
    ),
    p2: (
      <div>
        <p className="tc-prog">Step 2 of 4 — What we file</p>
        <h2 className="tc-h1">What we'll file — and the rules that come with it</h2>
        <p className="tc-lead">The networks you'll be sending texts to group messaging into categories, and <b>each category carries its own rules</b>. Naming yours isn't a limit — it's how you know which of their rules apply to you.</p>
        <p className="tc-lead">For a personal number we file the lightest category there is: <b>everyday conversation</b> — texting people 1-on-1 like any phone (friends, family, clients).</p>
        <div className="tc-note g"><b>Your rules — the complete list:</b><br />✓ Don't text the banned topics (next screen)<br />✓ People can stop your texts anytime — we handle STOP automatically<br />That's all. No other conditions apply to conversation.</div>
        <div className="tc-sample"><span>Sample we file with the networks</span>“Hi Sarah, it's Nicolas — confirming tomorrow 2pm works. Reply here if you need to move it. Reply STOP to opt out.”</div>
        <button className="tc-cta" onClick={() => setScreen('p3')}>Looks right →</button>
        <button className="tc-link" onClick={() => setScreen('p1')}>← Back</button>
      </div>
    ),
    p3: (
      <div>
        <p className="tc-prog">Step 3 of 4 — The promise</p>
        <h2 className="tc-h1">What you won't text</h2>
        <p className="tc-lead">US networks ban certain topics from texting for everyone:</p>
        <div className="tc-note r">{BANNED.map(b => <div key={b}>✗ {b}</div>)}</div>
        <label className="tc-attest"><input type="checkbox" defaultChecked /> I won't text about any of these</label>
        <button className="tc-cta" onClick={() => setScreen('p4')}>Continue →</button>
        <button className="tc-link" onClick={() => setScreen('p2')}>← Back</button>
      </div>
    ),
    p4: (
      <div>
        <p className="tc-prog">Step 4 of 4 — Confirm</p>
        <h2 className="tc-h1">Confirm &amp; turn on texting</h2>
        <div className="tc-fees">
          <div><span>One-time identity check (network fee)</span><span>$4.00</span></div>
          <div><span>Texting approval (network fee)</span><span>$2.00 / mo</span></div>
          <div><span>NumFree handling</span><span>$0.00</span></div>
          <div className="tot"><span>Due today</span><span>$6.00</span></div>
        </div>
        <p className="tc-help">Network fees, passed through. Separate from your number's monthly price. A retry never double-charges. You'll verify your mobile with a texted code right after submitting.</p>
        <button className="tc-cta" data-testid="tc-submit" disabled={submitting} onClick={() => submit({})}>{submitting ? 'Submitting…' : 'Submit — we take it from here →'}</button>
        <button className="tc-link" onClick={() => setScreen('p3')}>← Back</button>
      </div>
    ),
    b1: (
      <div data-testid="tc-b1">
        <p className="tc-prog">Step 1 of 3 — The organization</p>
        <h2 className="tc-h1">About the organization</h2>
        <div className="tc-fld"><label>Country of registration</label><input value={bizCountry} onChange={e => setBizCountry(e.target.value)} /></div>
        <div className="tc-fld"><label>Legal name — exactly as on the tax letter</label>
          <input data-testid="tc-bizname" value={bizName} onChange={e => setBizName(e.target.value)} placeholder="Acme Coffee LLC" />
          <p className="tc-help">One-person LLC? Copy the second name line too (it often reads “% SOLE MBR”).</p></div>
        <div className="tc-fld"><label>Business ID number{bizCountry === 'United States' ? ' (from your IRS letter)' : ''}</label><input placeholder={bizCountry === 'United States' ? '12-3456789' : 'Your country’s business registration number'} /></div>
        <div className="tc-note w">⛔ Brand-new ID? Fresh tax IDs take 30–90 days to appear in the registries the networks check — we'll tell you when yours is safe to submit instead of letting it bounce.</div>
        <div className="tc-note i">🧭 No tax registration ID? If you're personally the account holder, <button className="tc-inline" onClick={() => { setHolder('person'); setScreen('p1'); }}>the personal path is yours — switch</button>.</div>
        <button className="tc-cta" onClick={() => setScreen('b2')}>Continue →</button>
        <button className="tc-link" onClick={() => setScreen('router')}>← Back</button>
      </div>
    ),
    b2: (
      <div>
        <p className="tc-prog">Step 2 of 3 — What you'll use texting for</p>
        <h2 className="tc-h1">What will you text about?</h2>
        <p className="tc-lead">The networks you'll be sending to attach different rules to each category — picking yours tells you exactly which apply.</p>
        <div className="tc-chips">{['Appointments & reminders', 'Order updates', 'Customer support', 'Promotions'].map((c, i) => <span key={c} className={`tc-chip ${i < 2 ? 'on' : ''}`}>{c}</span>)}</div>
        <div className="tc-sample"><span>Samples we wrote from your picks</span>“Hi Anna — reminder: your appointment is tomorrow at 2pm. Reply STOP to opt out.”</div>
        <div className="tc-note g"><b>Your number still texts like a phone.</b> Family, friends, one-off conversations — none of that goes away. The category only describes your business-style messages, and tells you which extra rules apply to those (and only those).</div>
        <button className="tc-cta" onClick={() => setScreen('b3')}>Continue →</button>
        <button className="tc-link" onClick={() => setScreen('b1')}>← Back</button>
      </div>
    ),
    b3: (
      <div>
        <p className="tc-prog">Step 3 of 3 — Confirm</p>
        <h2 className="tc-h1">Confirm &amp; submit</h2>
        <div className="tc-fees">
          <div><span>One-time identity check (network fee)</span><span>$4.00</span></div>
          <div><span>Texting registration (network fee)</span><span>$15.00</span></div>
          <div><span>Monthly network fee</span><span>$2.00 / mo</span></div>
          <div className="tot"><span>Due today</span><span>$21.00</span></div>
        </div>
        <p className="tc-help">Pass-through network fees — itemized, nothing hidden. A retry never double-charges.</p>
        <button className="tc-cta" data-testid="tc-submit" disabled={submitting} onClick={() => submit({ businessName: bizName, country: bizCountry })}>{submitting ? 'Submitting…' : 'Submit — we take it from here →'}</button>
        <button className="tc-link" onClick={() => setScreen('b2')}>← Back</button>
      </div>
    ),
    intl: (
      <div>
        <h2 className="tc-h1">Texting numbers outside the US &amp; Canada</h2>
        <p className="tc-lead">This setup enables sending to <b>US mobile operators</b>. Every other country's networks have <b>their own rules</b> for accepting messages from verified senders.</p>
        <div className="tc-note i">We bring destinations online country by country. When you text an international number, the app tells you right there whether that country is available — no surprises, no silent failures.</div>
        <div className="tc-note g">✓ Receiving texts and calls from anywhere in the world already works — nothing to set up.</div>
        <button className="tc-cta" onClick={() => setScreen(regs && regs.length ? 'dash' : 'intro')}>Got it →</button>
      </div>
    ),
    dash: (
      <div data-testid="tc-dash">
        <h2 className="tc-h1">Texting setup</h2>
        {(regs || []).map(r => (
          <div key={r.regId} className="tc-reg" data-testid={`tc-reg-${r.status}`}>
            <div className="tc-reg-top">
              <span>{r.accountHolder === 'organization' ? 'Organization' : 'Personal'} · {r.regId.slice(0, 10)}…</span>
              <span className={`tc-pill ${r.status}`}>{r.status === 'pending' ? '● In review' : r.status === 'approved' ? '● Texting is on' : r.status === 'action_required' ? '● Action needed' : r.status}</span>
            </div>
            {r.status === 'pending' && <p className="tc-lead">⏳ Getting your texting approved — usually a few days. Calls, receiving and your AI keep working. We'll update you in Messages.</p>}
            {r.status === 'approved' && <p className="tc-lead">🎉 Your number can now send texts to US mobile operators.</p>}
            {r.status === 'action_required' && (
              <div>
                <div className="tc-note r"><b>The network said:</b><br />“{r.reason}”</div>
                <div className="tc-fld"><label>Corrected legal name</label>
                  <input data-testid="tc-fixname" value={bizName} onChange={e => setBizName(e.target.value)} placeholder="ACME COFFEE LLC % SOLE MBR" /></div>
                <button className="tc-cta" data-testid="tc-fix" disabled={submitting} onClick={() => resubmit(r.regId)}>{submitting ? 'Resubmitting…' : 'Fix it now → resubmit (free)'}</button>
              </div>
            )}
          </div>
        ))}
        <button className="tc-cta ghost" onClick={() => setScreen('intro')}>Set up another number →</button>
        <button className="tc-link" onClick={() => setScreen('intl')}>Texting numbers outside the US &amp; Canada?</button>
      </div>
    )
  };

  if (regs === null) return <div className="tc-wrap"><p className="tc-lead">Loading…</p></div>;
  return (
    <div className="tc-wrap" data-testid="trust-center">
      {error && <div className="tc-note r">{error}</div>}
      {screens[screen] || screens.intro}
    </div>
  );
};

export default TrustCenterWizard;
